<template>
  <div class="pt-2 h-full flex flex-col overflow-hidden">
    <!-- Main Content -->
    <!-- Header -->

    <div class="flex-1 overflow-hidden">
      <v-row>
        <!-- Left Sidebar - Branch List -->
        <v-col cols="12" md="4" lg="3" class="pr-0 overflow-hidden">
          <v-card flat class="h-full form-card flex flex-col overflow-hidden">
            <v-card-title class="text-base font-bold text-gray-800 flex-shrink-0 overflow-hidden">
              Office Locations
              <div class="flex justify-between w-full">
                <v-text-field
                  label="Search Office Location"
                  class="text-input flex-grow"
                  v-model="searchTerm"
                  outlined
                  flat
                  hide-details
                  solo
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search Office Location"
                >
                </v-text-field>
              </div>
            </v-card-title>

            <v-card-text class="pt-0 flex-1 overflow-hidden">
              <div class="space-y-3 h-full overflow-x-hidden">
                <div
                  v-for="branch in filteredBranches"
                  :key="branch._id"
                  class="cursor-pointer flex justify-between items-center relative p-2 overflow-hidden"
                  :class="[
                    selectedBranch && selectedBranch._id === branch._id
                      ? 'border border-blue-500 bg-white'
                      : 'border border-gray-200'
                  ]"
                  outlined
                  @click="selectBranch(branch)"
                >
                  <v-card-text class="py-3 overflow-hidden">
                    <h3 class="font-semibold text-gray-800 mb-1 overflow-hidden">
                      {{ branch?.branchName }}
                    </h3>
                  </v-card-text>
                  <div class="flex items-center absolute top-2 right-2 gap-2 my-4">
                    <v-icon @click="openQrCode(branch)" small>mdi-qrcode</v-icon>
                    <v-icon
                      v-if="filteredBranches.length && filteredBranches.length > 1"
                      color="error"
                      small
                      @click.stop="deleteBranch(branch)"
                      class="cursor-pointer hover:opacity-80"
                    >
                      mdi-delete
                    </v-icon>
                    <v-icon
                      color="primary"
                      small
                      @click.stop="editBranch(branch)"
                      class="cursor-pointer hover:opacity-80"
                    >
                      mdi-pencil
                    </v-icon>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- <v-divider vertical></v-divider> -->
        <!-- Right Panel - Branch Details -->
        <v-col cols="12" md="8" lg="9" class="form-content overflow-hidden">
          <v-card class="h-full form-card flex flex-col overflow-hidden" v-if="selectedBranch" flat>
            <!-- Branch Header -->
            <v-card-title class="border-b border-gray-200 pb-4 flex-shrink-0 overflow-hidden">
              <div class="flex-1 overflow-hidden">
                <h2 class="text-base font-bold text-gray-800 mb-2 overflow-hidden">
                  {{ selectedBranch?.branchName }}
                </h2>
                <div class="space-y-1 text-xs text-gray-600 overflow-hidden">
                  <p class="overflow-hidden">
                    Description : {{ selectedBranch.branchDescription }}
                  </p>
                  <p class="overflow-hidden" v-if="selectedBranch.branchLocation?.addressLine1">
                    Address : {{ selectedBranch.branchLocation?.addressLine1 }}
                  </p>
                  <p class="overflow-hidden" v-if="selectedBranch.branchHead">
                    Branch Head : {{ selectedBranch.branchHead }}
                  </p>
                  <div v-if="formattedAuthorizedSignature" class="mt-2">
                    <p class="font-semibold">Authorized Signature:</p>
                    <img
                      :src="formattedAuthorizedSignature"
                      alt="Authorized Signature"
                      class="w-32 h-16 object-contain border rounded mt-1"
                    />
                  </div>
                </div>
              </div>
              <v-btn
                v-if="selectedBranch.teams && selectedBranch.teams.length > 0"
                color="primary"
                @click="showTeamModal = true"
                class="font-medium overflow-hidden"
                style="text-transform: none"
                outlined
              >
                <v-icon left>mdi-plus</v-icon>
                Add Team
              </v-btn>
            </v-card-title>

            <v-card-text class="flex-1 overflow-hidden">
              <!-- Team Management -->
              <div class="mt-4 h-full overflow-hidden">
                <div
                  v-if="selectedBranch.teams && selectedBranch.teams.length > 0"
                  class="h-full flex flex-col overflow-hidden"
                >
                  <h3 class="text-lg font-semibold mb-4 flex-shrink-0 overflow-hidden">Teams</h3>

                  <!-- Team List -->
                  <div class="space-y-2 mb-6 flex-1 overflow-x-hidden">
                    <v-card
                      v-for="team in selectedBranch.teams"
                      :key="team.id"
                      :class="[
                        'team-item-hover cursor-pointer transition-colors duration-200 overflow-hidden',
                        selectedTeam?.id === team.id ? 'bg-blue-50 border-blue-200' : ''
                      ]"
                      outlined
                      @click="selectTeam(team)"
                    >
                      <v-card-text class="py-3 overflow-hidden">
                        <h4 class="font-semibold text-gray-800 overflow-hidden">
                          {{ team?.name }}
                        </h4>
                        <p class="text-sm text-gray-600 overflow-hidden">
                          Team Lead:
                          <template v-if="team?.lead">
                            <UserAvatar
                              :user="getMemberDetails(team.lead)"
                              :size="20"
                              :show-name="true"
                              :tooltip-enabled="true"
                              name-class="text-sm ml-1"
                              class="inline-flex"
                            />
                          </template>
                          <template v-else> Not assigned </template>
                          | {{ team?.members?.length || 0 }} members
                        </p>

                        <p class="text-sm text-gray-600 overflow-hidden" v-if="team?.useCase">
                          Use Case: {{ team.useCase }}
                        </p>
                        <p class="text-sm text-gray-600 overflow-hidden" v-if="team?.subCase">
                          Sub Case: {{ getSubCaseNameById(team.subCase) }}
                        </p>

                        <p class="text-sm text-gray-600 overflow-hidden" v-if="team?.useCase">
                          Use Case: {{ team.useCase }}
                        </p>
                        <p class="text-sm text-gray-600 overflow-hidden" v-if="team?.subCase">
                          Sub Case: {{ getSubCaseNameById(team.subCase) }}
                        </p>
                      </v-card-text>
                      <div class="flex items-center absolute top-2 right-2 gap-2">
                        <v-icon
                          small
                          color="error"
                          @click="deleteTeam(team)"
                          :disabled="isDeleting"
                          v-if="selectedBranch.teams && selectedBranch.teams?.length > 1"
                        >
                          {{ isDeleting ? 'mdi-loading mdi-spin' : 'mdi-delete' }}
                        </v-icon>
                        <v-icon small @click="editTeam(team)" :disabled="isDeleting"
                          >mdi-pencil</v-icon
                        >
                      </div>
                    </v-card>
                  </div>
                </div>

                <!-- Show NoData component when no teams exist -->
                <div v-else>
                  <NoData
                    title="No Teams Found"
                    subTitle="This branch doesn't have any teams yet. Create your first team to get started."
                    btnText="Create Team"
                    :btnAction="() => (showTeamModal = true)"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- No Branch Selected -->
          <v-card
            v-else
            class="h-full flex items-center justify-center overflow-hidden form-card"
            flat
          >
            <v-card-text class="text-center overflow-hidden">
              <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-office-building</v-icon>
              <p class="text-lg text-gray-500 overflow-hidden">Select a branch to view details</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Add Branch Modal -->
    <v-dialog v-model="showBranchModal" max-width="700px" persistent>
      <v-card>
        <!-- Fixed tabs using proper v-tabs component with unified configuration -->
        <v-tabs v-model="branchTabs" class="">
          <v-tab v-for="(tab, index) in dynamicBranchTabItems" :key="index">
            {{ tab.name }}
          </v-tab>
          <v-spacer></v-spacer>
          <v-icon @click="closeBranchModal" class="mr-2 mt-2">mdi-close</v-icon>
        </v-tabs>
        <v-divider></v-divider>
        <div class="ma-4">
          <!-- Add Branch Tab Content -->
          <v-form ref="branchForm" v-model="branchFormValid" v-show="branchTabs === 0">
            <v-text-field
              v-model="newBranch.name"
              label="Branch Name *"
              dense
              hide-details
              class="mb-2"
              outlined
              :rules="[(v) => !!v || 'Branch name is required']"
              @input="isBranchEdited = true"
              prepend-inner-icon="mdi-office-building"
            ></v-text-field>
            <v-textarea
              v-model="newBranch.description"
              label="Description"
              dense
              outlined
              rows="3"
              hide-details
              class="mb-4"
              @input="isBranchEdited = true"
              prepend-inner-icon="mdi-text"
            ></v-textarea>
            <!-- Authorized Signature Upload -->
            <div>
              <v-select
                class="w-full"
                v-model="newBranch.branchHead"
                :items="availableMembers"
                label="Branch Head"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-account-tie"
                @change="isBranchEdited = true"
              ></v-select>
              <p class="text-sm font-bold text-gray-700 mb-4">Authorized Signature</p>
              <div class="flex items-center gap-4">
                <v-file-input
                  v-model="signatureFile"
                  label="Upload Signature"
                  outlined
                  dense
                  hide-details
                  accept="image/*"
                  :disabled="isSaving"
                  class="flex-grow w-4/5"
                  prepend-inner-icon="mdi-file-image-outline"
                  prepend-icon=""
                ></v-file-input>

                <div v-if="newBranch.authorizedSignature" class="flex items-center">
                  <img
                    :src="getFormattedSignatureUrl(newBranch.authorizedSignature)"
                    alt="Authorized Signature"
                    class="w-16 h-16 object-contain border rounded"
                  />
                  <v-icon small color="error" @click="removeSignature" class="ml-2 cursor-pointer">
                    mdi-delete
                  </v-icon>
                </div>
              </div>
              <p class="text-xs text-gray-500 mt-2">
                Upload an image file (JPG, PNG) for the authorized signature
              </p>
            </div>
            <v-divider class="my-2"></v-divider>

            <CommonAutoComplete
              :addressLine1="newBranch.address"
              @selected-place="handleSelectedPlace"
            />
            <div v-if="newBranch.address" class="text-base font-semibold text-gray-600 mt-1">
              <v-icon small class="mr-1">mdi-map-marker</v-icon>
              Selected address: {{ newBranch.address }}
            </div>
            <v-checkbox
              v-model="newBranch.geoFenceStatus"
              label="Set geo fence radius"
              dense
              hide-details
              class="mb-4"
              @change="isBranchEdited = true"
              v-show="
                isGeoFeatureVisible('ENABLE_BRANCH_WISE_GEO_FENCING_MODE') && newBranch.address
              "
              prepend-icon="mdi-map-clock-outline"
            ></v-checkbox>
            <div v-if="newBranch.geoFenceStatus" class="mb-4">
              <p class="text-sm text-gray-600 mb-2">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                Geo-fence configuration will be set on map after saving the branch.
              </p>
              <!-- Google Maps Container -->
              <div
                ref="branchMap"
                style="width: 100%; height: 300px; border: 1px solid #ccc; border-radius: 4px"
                class="mb-4"
              ></div>
              <v-slider
                v-if="newBranch.geoFencingData && newBranch.geoFencingData.radius"
                v-model="newBranch.geoFencingData.radius"
                :min="200"
                :max="2000"
                label="Radius (meters)"
                thumb-label="always"
                class="mt-4"
                @change="updateMapRadius"
                prepend-icon="mdi-ruler"
              ></v-slider>
            </div>
          </v-form>
          <!-- Bulk Create Tab Content -->
          <div v-show="branchTabs === 1">
            <div class="text-center py-8">
              <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-file-upload</v-icon>
              <p class="text-lg text-gray-500">Bulk Create functionality coming soon</p>
              <p class="text-sm text-gray-400 mt-2">
                Upload a CSV file to create multiple branches at once
              </p>
              <v-btn color="primary" class="mt-4" outlined disabled> Upload CSV </v-btn>
            </div>
          </div>
        </div>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 pt-3">
          <v-spacer></v-spacer>
          <v-btn text @click="closeBranchModal" :disabled="isSaving" style="text-transform: none"
            >Cancel</v-btn
          >
          <!-- :disabled="
              (branchTabs === 0 && (!branchFormValid || (newBranch.id && !isBranchEdited))) ||
              branchTabs === 1 ||
              isSaving
            " -->
          <v-btn
            color="primary"
            @click="saveBranch"
            :loading="isSaving"
            style="text-transform: none"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Team Modal -->
    <v-dialog v-model="showTeamModal" persistent width="70rem">
      <v-card>
        <div class="p-4 d-flex justify-space-between align-center border-bottom">
          <h3 class="form-title">{{ newTeam.id ? 'Edit Team' : 'Create Team' }}</h3>

          <v-icon class="" @click="closeTeamModal">mdi-close</v-icon>
        </div>
        <v-divider></v-divider>
        <div class="form-content">
          <v-form ref="teamForm" v-model="teamFormValid">
            <div class="p-4 pt-2">
              <p class="text-sm pb-2 font-bold text-gray-700">Basic info</p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-2">
                <v-text-field
                  v-model="newTeam.name"
                  label="Team Name *"
                  dense
                  outlined
                  hide-details
                  :rules="[
                    (v) => !!v || 'Team name is required',
                    (v) => (v && v.length >= 2) || 'Team name must be at least 2 characters'
                  ]"
                  @input="isTeamEdited = true"
                  prepend-inner-icon="mdi-account-group"
                ></v-text-field>
                <v-text-field
                  v-model="newTeam.description"
                  label="Description"
                  dense
                  outlined
                  hide-details
                  rows="3"
                  @input="isTeamEdited = true"
                  prepend-inner-icon="mdi-text"
                ></v-text-field>
              </div>
            </div>
            <v-divider></v-divider>
            <div class="p-4 pt-1">
              <p class="text-sm pt-2 font-bold text-gray-700">Members</p>
              <div class="flex justify-between gap-4 mt-2">
                <v-select
                  class="w-full md:w-1/2"
                  v-model="newTeam.lead"
                  :items="availableMembers"
                  label="Select Team Lead"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-account-tie"
                ></v-select>

                <v-select
                  class="w-full md:w-1/2"
                  v-model="newMember"
                  :items="availableMembersToAdd"
                  label="Select a member"
                  placeholder="Select a member"
                  outlined
                  dense
                  hide-details
                  @change="addMemberToNewTeam"
                  prepend-inner-icon="mdi-account-plus"
                  :key="memberSelectKey"
                ></v-select>
              </div>
              <!-- <v-divider></v-divider> -->
              <div
                v-for="(member, index) in members"
                :key="index"
                class="flex justify-between items-center border-t bg-gray-50 rounded my-2 py-2 px-2"
              >
                <UserAvatar
                  :user="getMemberDetails(member)"
                  :size="28"
                  :show-name="true"
                  :tooltip-enabled="true"
                  name-class="text-sm ml-2"
                  class="inline-flex"
                />
                <v-icon small @click="removeMemberFromNewTeam(index)" class="ml-1 cursor-pointer">
                  mdi-close
                </v-icon>
              </div>
            </div>
            <v-divider class="mb-4" v-if="isCallingeConfigured"></v-divider>
            <div v-if="isCallingeConfigured" class="p-4 pt-1">
              <p class="text-sm font-bold text-gray-700">Assign Virtual Number</p>
              <div v-if="phoneNumbers && phoneNumbers.length > 0" class="mt-4">
                <v-autocomplete
                  v-model="newTeam.connectVirtualNumber"
                  :items="phoneNumbers"
                  item-text="phoneNumber"
                  item-value="_id"
                  outlined
                  hide-details
                  dense
                  label="Select Virtual Number For Telecalling"
                  @change="isTeamEdited = true"
                  prepend-inner-icon="mdi-phone"
                ></v-autocomplete>
              </div>
              <div v-else class="text-center py-4">
                <div>
                  <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-phone-off</v-icon>
                  <p class="text-gray-600 mb-4">No virtual numbers available</p>
                  <v-btn
                    color="primary"
                    class="normal-case"
                    @click="navigateToTelecallerIntegration"
                    outlined
                  >
                    <v-icon left>mdi-cog</v-icon>
                    Configure Telecaller Integration
                  </v-btn>
                </div>
              </div>
            </div>
            <v-divider></v-divider>
            <div class="p-4">
              <p class="text-sm font-bold text-gray-700">Assign Use Case</p>
              <!-- Use Case Selection -->
              <div class="mt-4">
                <v-select
                  v-model="selectedUseCase"
                  :items="useCaseData"
                  item-text="text"
                  item-value="value"
                  label="Select Use Case"
                  outlined
                  dense
                  hide-details
                  @change="handleUseCaseChange"
                  prepend-inner-icon="mdi-folder-star"
                ></v-select>

                <!-- Sub Case Selection (only shown when a use case is selected) -->
                <v-select
                  v-model="newTeam.subCase"
                  :items="filteredSubCases"
                  item-text="name"
                  item-value="_id"
                  label="Select Sub Case"
                  class="pt-2"
                  outlined
                  dense
                  hide-details
                  :disabled="!selectedUseCase"
                  v-if="selectedUseCase"
                  prepend-inner-icon="mdi-folder-open"
                ></v-select>
              </div>
            </div>
          </v-form>
        </div>
        <v-divider></v-divider>
        <div class="p-4 flex justify-end">
          <v-btn text @click="closeTeamModal" :disabled="isSaving" style="text-transform: none"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            @click="saveTeam"
            :disabled="!teamFormValid || isSaving || (newTeam.id && !isTeamEdited)"
            :loading="isSaving"
            style="text-transform: none"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <!-- Delete Confirmation Dialog -->
    <AlertPopUp
      :show-confirmation-dialog="showDeleteDialog"
      confirmation-title="Confirm Delete"
      :confirmation-message="
        deleteErrorMessage ||
        (deleteType === 'branch'
          ? 'Are you sure you want to delete this branch? This action cannot be undone.'
          : 'Are you sure you want to delete this team? This action cannot be undone.')
      "
      confirmation-action-text="Delete"
      confirmation-action-color="error"
      :perform-action="confirmDelete"
      :cancel="cancelDelete"
      :error-message="deleteErrorMessage"
    />
    <v-dialog v-model="qrDialog" max-width="500px" class="dark-bg-custom" persistent>
      <v-card class="dark-bg-custom">
        <div class="flex justify-between p-4">
          <span class="text-md font-semibold">Organization QR Code(with geofencing)</span>
          <v-icon @click="qrDialog = false">mdi-close</v-icon>
        </div>
        <v-divider></v-divider>
        <v-card-text>
          <QRCode :qrValue="qrValue" :width="450" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global google */
import NoData from '@/components/NoData.vue'
import { convertToTitleCase, isModuleFeatureAllowed } from '@/utils/common'
import UserAvatar from '@/components/UserAvatar.vue'
import { axiosUrl } from '@/utils/workforceApi'
import CommonAutoComplete from '@/components/CommonAutoComplete.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import QRCode from '@/components/Workforce/Settings/OrgConfigComponents/qrCode.vue'
export default {
  name: 'BranchTeam',
  components: {
    NoData,
    CommonAutoComplete,
    UserAvatar,
    AlertPopUp,
    QRCode
  },
  props: {
    orgConfig: Object
  },
  data() {
    return {
      // Modal states
      showBranchModal: false,
      showTeamModal: false,
      branchFormValid: false,
      teamFormValid: false,
      showDialog: false,
      searchTerm: '',
      branchTabs: 0,
      teamTabs: 0,
      addressLine1: null,

      // Google Maps
      branchMap: null,
      branchMapMarker: null,
      branchMapCircle: null,
      googleMapsLoaded: false,

      // Unified tabs configuration
      branchTabItems: [
        { key: 'addBranch', name: 'Add Branch' },
        { key: 'bulkCreate', name: 'Bulk Create' }
      ],
      teamTabItems: [{ key: 'addTeam', name: 'Create Team' }],
      qrValue: '',
      // Selected items
      selectedBranch: null,
      selectedTeam: null,
      newMember: null,
      selectedUseCase: null, // New property for selected use case
      qrDialog: false,
      // Form data
      newBranch: {
        name: '',
        description: '',
        address: '',
        branchLocation: {},
        geoFencingData: {
          radius: 500
        },
        geoFenceStatus: false,
        authorizedSignature: '',
        branchHead: '' // Add branchHead property
      },
      selectedAddress: null,
      newTeam: {
        name: '',
        description: '',
        lead: '',
        connectVirtualNumber: null,
        dataCategories: [],
        // linkedLicense: null,
        useCase: '', // Add useCase property
        subCase: '' // Add subCase property
      },
      members: [],
      // Available options
      availableManagers: [],
      availableMembers: [],
      managers: [],
      phoneNumbers: [],
      allSubCases: [], // For storing all subcases
      allUseCases: [], // For storing all use cases
      useCaseData: [], // New property to store use case data from fetchUseCaseData

      // Add a flag to track if all subcases have been loaded
      hasAllSubCasesLoaded: false,

      // Email validation rules
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],

      // Data from API
      branches: [],
      teams: [],
      orgCode: this.$storage.getUniversal('organization_code'),
      baseUrl: process.env.VUE_APP_S3_BASE_URL, // Add baseUrl for S3 images

      // Loading states
      isDeleting: false,
      isSaving: false,
      isCreating: false,
      isUpdating: false,

      // Edit tracking flags
      isBranchEdited: false,
      isTeamEdited: false,
      isTeamDetailsEdited: false,

      // Key for forcing re-render of member select
      memberSelectKey: 0,

      // Add signature file property
      signatureFile: null,

      // Confirmation dialog states
      showDeleteDialog: false,
      itemToDelete: null,
      deleteType: null, // 'branch' or 'team'

      // Error message for deletion
      deleteErrorMessage: null
    }
  },
  computed: {
    branchFence() {
      return true
    },

    currentBranchTab() {
      return this.branchTabs
    },

    // Dynamic branch tab items
    dynamicBranchTabItems() {
      const tabs = [...this.branchTabItems]
      if (this.newBranch.id) {
        tabs[0].name = 'Edit Office Location'
      } else {
        tabs[0].name = 'Add Office Location'
      }
      return tabs
    },

    // Dynamic team tab items
    dynamicTeamTabItems() {
      const tabs = [...this.teamTabItems]
      if (this.newTeam.id) {
        tabs[0].name = 'Edit Team'
      } else {
        tabs[0].name = 'Create Team'
      }
      return tabs
    },

    filteredBranches() {
      if (!this.searchTerm) return this.branches
      return this.branches.filter((branch) =>
        branch.branchName.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    },
    isCallingeConfigured() {
      return isModuleFeatureAllowed('ADDONS', 'CALL_A_CUSTOMER')
    },
    availableMembersToAdd() {
      // For new team creation
      if (this.showTeamModal && !this.selectedTeam) {
        return this.availableMembers.filter((member) => {
          if (this.newTeam.lead && member === this.newTeam.lead) {
            return false
          }

          if (this.newTeam.members && this.newTeam.members.includes(member)) {
            return false
          }
          return true
        })
      }

      return this.availableMembers.filter((member) => {
        if (this.selectedTeam && this.selectedTeam.lead && member === this.selectedTeam.lead) {
          return false
        }

        if (
          this.selectedTeam &&
          this.selectedTeam.members &&
          this.selectedTeam.members.includes(member)
        ) {
          return false
        }
        return true
      })
    },

    // Filter subcases based on selected use case
    filteredSubCases() {
      if (!this.selectedUseCase || !this.allSubCases || this.allSubCases.length === 0) return []

      return this.allSubCases.filter((subCase) => subCase.useCase === this.selectedUseCase)
    },

    // Format authorized signature URL with S3 base URL
    formattedAuthorizedSignature() {
      if (!this.selectedBranch || !this.selectedBranch.authorizedSignature) return ''

      // If the signature already contains a full URL, return as is
      if (this.selectedBranch.authorizedSignature.startsWith('http')) {
        return this.selectedBranch.authorizedSignature
      }

      // Otherwise, prepend the S3 base URL
      return `${this.baseUrl || ''}${this.selectedBranch.authorizedSignature}`
    }
  },
  async mounted() {
    await this.fetchBranches()
    await this.fetchTeams()
    await this.getVoiceNumbers()
    await this.fetchManager()
    await this.fetchUseCaseData()
    await this.fetchAllSubCasesForAllUseCases() // Add this line to fetch all subcases
    if (this.branches.length > 0) {
      this.selectBranch(this.branches[0])
    }
  },
  watch: {
    'newTeam.lead'(newLead, oldLead) {
      // When a new lead is selected, remove them from the members array if they were previously added as a member
      if (newLead && this.members && this.members.includes(newLead)) {
        const index = this.members.indexOf(newLead)
        this.members.splice(index, 1)
      }
    },

    'selectedTeam.lead'(newLead, oldLead) {
      if (this.selectedTeam && newLead && this.selectedTeam.members.includes(newLead)) {
        const index = this.selectedTeam.members.indexOf(newLead)
        this.selectedTeam.members.splice(index, 1)
      }
    },

    selectedUseCase: {
      handler(newUseCase) {
        // Only fetch subcases for specific use case when creating/editing a team
        // Don't reset allSubCases if we already have all subcases loaded
        if (newUseCase && !this.hasAllSubCasesLoaded) {
          this.fetchAllUseCases()
        } else if (!newUseCase && !this.hasAllSubCasesLoaded) {
          this.allSubCases = []
        }
      },
      immediate: false
    },

    'newBranch.geoFenceStatus': {
      handler(newVal) {
        if (newVal && this.newBranch.address) {
          // Delay initialization to ensure DOM is ready
          this.$nextTick(() => {
            this.initBranchMap()
          })
        }
      }
    },

    'newBranch.address': {
      handler(newVal) {
        if (newVal && this.newBranch.geoFenceStatus) {
          // Reinitialize map when address changes and geo-fence is enabled
          this.$nextTick(() => {
            this.initBranchMap()
          })
        }
      }
    }
  },
  methods: {
    openQrCode(branch) {
      this.qrDialog = true
      // this.qrValue(branch)
      if (branch?.geoFencingData) {
        const { location, radius } = branch.geoFencingData || {}
        if (location || radius) {
          this.qrValue = `${process.env.VUE_APP_BASE_URL}/mark-attendance?orgCode=${this.orgCode}&lat=${location.lat}&lng=${location.lng}&radius=${radius}`
        }
      }
      this.qrValue = `${process.env.VUE_APP_BASE_URL}/mark-attendance?orgCode=${this.orgCode}`
    },
    async fetchBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches || []
        // Ensure each branch has the required properties
        this.branches = this.branches.map((branch) => ({
          ...branch,
          branchAddress: branch.branchAddress || '',
          geoFencingData: branch.geoFencingData || {},
          geoFenceStatus: branch.geoFenceStatus !== undefined ? branch.geoFenceStatus : true,
          teams: this.getTeamsByBranch(branch._id)
        }))
      } catch (error) {
        console.error('Error fetching branches:', error)
        this.$toast.error('Failed to fetch branches')
      }
    },
    async fetchAllUseCases() {
      try {
        // Only fetch if a use case is selected
        if (!this.selectedUseCase) {
          // Only reset if we haven't loaded all subcases
          if (!this.hasAllSubCasesLoaded) {
            this.allSubCases = []
          }
          return
        }

        const res = await this.$axios.get(
          `/orgSetup/extend-usecase?useCase=${this.selectedUseCase}`
        )
        const subCases = res.data?.data || []
        this.allSubCases = subCases.map((subCase) => ({
          ...subCase,
          useCase: this.selectedUseCase
        }))
      } catch (error) {
        console.log(error)
        this.$toast.error('Failed to fetch use cases')
        // Only reset if we haven't loaded all subcases
        if (!this.hasAllSubCasesLoaded) {
          this.allSubCases = []
        }
      }
    },

    async fetchUseCaseData() {
      try {
        const res = await this.$axios.get('/orgSetup/loan-management-field-mapping/data')
        this.useCaseData = Object.keys(res.data?.data).map((item) => {
          return {
            text: convertToTitleCase(item),
            value: item
          }
        })
        // console.log(this.useCaseData)
      } catch (error) {
        console.log(error)
        this.$toast.error('Failed to fetch use case data')
      }
    },

    selectBranch(branch) {
      const branchWithDefaults = {
        ...branch,
        branchAddress: branch.branchAddress || '',
        geoFencingData: branch.geoFencingData || {},
        geoFenceStatus: branch.geoFenceStatus || false
      }

      this.selectedBranch = { ...branchWithDefaults }

      if (this.selectedBranch) {
        this.$set(this.selectedBranch, 'teams', this.getTeamsByBranch(this.selectedBranch._id))

        if (this.selectedBranch.teams?.length > 0) {
          this.selectTeam(this.selectedBranch.teams[0])
        } else {
          this.selectedTeam = null
        }
      }
    },
    async fetchTeams() {
      try {
        const response = await this.$axios.get(axiosUrl.teams.getTeams)
        this.teams = response.data.teams.map((team) => ({
          ...team,
          disabled: true,
          id: team._id,
          name: team.teamName,
          lead: team.teamLead || '',
          members: team.members || [],
          connectVirtualNumber: team.connectVirtualNumber || null,
          useCase: team.useCase || '',
          subCase: team.subCase || ''
        }))

        // Update branches with their teams
        if (this.branches.length > 0 && this.teams.length > 0) {
          this.branches.forEach((branch) => {
            branch.teams = this.getTeamsByBranch(branch._id)
          })
        }
      } catch (error) {
        console.error('Error fetching teams:', error)
      }
    },

    getTeamsByBranch(branchId) {
      if (!branchId) {
        console.log('No branchId provided to getTeamsByBranch')
        return []
      }

      const filteredTeams = this.teams.filter((team) => team.branch === branchId)
      return filteredTeams
    },

    selectTeam(team) {
      this.selectedTeam = { ...team }
      if (!this.selectedTeam.members) {
        this.$set(this.selectedTeam, 'members', [])
      }
    },

    addMemberToNewTeam() {
      if (this.newMember && this.newTeam && !this.newTeam.members.includes(this.newMember)) {
        // If the member being added is currently the team lead, remove lead status
        if (this.newTeam.lead === this.newMember) {
          this.newTeam.lead = ''
        }
        this.newTeam.members.push(this.newMember)
        this.newMember = null
      }
    },

    removeMemberFromNewTeam(index) {
      if (!this.newTeam || !this.members) return
      this.members.splice(index, 1)
    },

    async deleteTeam(team) {
      if (!team || this.isDeleting) return
      // Set the team to delete and show confirmation dialog
      this.itemToDelete = team
      this.deleteType = 'team'
      this.deleteErrorMessage = null // Clear any previous error message
      this.showDeleteDialog = true
    },
    async deleteBranch(branch) {
      if (!branch) return
      // Set the branch to delete and show confirmation dialog
      this.itemToDelete = branch
      this.deleteType = 'branch'
      this.deleteErrorMessage = null // Clear any previous error message
      this.showDeleteDialog = true
    },

    confirmDelete() {
      if (!this.itemToDelete) return
      this.showDeleteDialog = false
      if (this.deleteType === 'branch') {
        this.performDeleteBranch(this.itemToDelete)
      } else if (this.deleteType === 'team') {
        this.performDeleteTeam(this.itemToDelete)
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false
      this.itemToDelete = null
      this.deleteType = null
      this.deleteErrorMessage = null
    },

    async performDeleteTeam(team) {
      try {
        this.isDeleting = true
        this.deleteErrorMessage = null

        const response = await this.$axios.delete(`${axiosUrl.teams.deleteTeams}/${team._id}`)
        const successMessage = response?.data?.message || 'Team deleted successfully'
        const updatedTeams = this.teams.filter((t) => t._id !== team._id)
        this.$set(this, 'teams', updatedTeams)
        if (this.selectedBranch) {
          const updatedBranchTeams = this.selectedBranch.teams.filter((t) => t._id !== team._id)
          this.$set(this.selectedBranch, 'teams', updatedBranchTeams)
        }

        // Clear selected team if it's the one being deleted
        if (this.selectedTeam && this.selectedTeam._id === team._id) {
          this.selectedTeam = null
        }

        this.$toast.success(successMessage)
      } catch (error) {
        console.error('Error deleting team:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete team'
        this.deleteErrorMessage = errorMessage
        // Keep the dialog open to show the error message
        this.showDeleteDialog = true
      } finally {
        this.isDeleting = false
        if (!this.deleteErrorMessage) {
          this.itemToDelete = null
          this.deleteType = null
        }
      }
    },

    async performDeleteBranch(branch) {
      try {
        this.deleteErrorMessage = null
        const response = await this.$axios.delete(
          `${axiosUrl.branches.deleteBranches}/${branch._id}`
        )
        const successMessage = response?.data?.message || 'Branch deleted successfully'
        this.$toast.success(successMessage)
        await this.fetchBranches()
      } catch (error) {
        console.error('Error deleting branch:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete branch'
        this.deleteErrorMessage = errorMessage
        // Keep the dialog open to show the error message
        this.showDeleteDialog = true
      } finally {
        if (!this.deleteErrorMessage) {
          this.itemToDelete = null
          this.deleteType = null
        }
      }
    },

    // Modal methods
    closeBranchModal() {
      this.showBranchModal = false
      this.resetBranchForm()
    },

    closeTeamModal() {
      this.showTeamModal = false
      this.resetTeamForm()
    },

    resetBranchForm() {
      this.newBranch = {
        name: '',
        description: '',
        authorizedSignature: '',
        branchHead: '' // Add branchHead to reset
      }
      this.isBranchEdited = false
      if (this.$refs.branchForm) {
        this.$refs.branchForm.resetValidation()
      }
    },

    resetTeamForm() {
      this.newTeam = {
        name: '',
        description: '',
        lead: '',
        members: [],
        connectVirtualNumber: null,
        useCase: '',
        subCase: ''
      }
      this.selectedUseCase = ''
      this.newMember = null
      this.isTeamEdited = false
      this.signatureFile = null
      if (this.$refs.teamForm) {
        this.$refs.teamForm.resetValidation()
      }
    },

    handleSelectedPlace(place) {
      if (this.showBranchModal && this.newBranch) {
        this.newBranch.address = place.formatted_address

        if (place.location) {
          // Initialize geoFencingData if it doesn't exist
          if (!this.newBranch.geoFencingData) {
            this.newBranch.geoFencingData = {
              radius: 500
            }
          }

          // Store the location
          this.newBranch.geoFencingData.location = {
            lat: place.location.lat(),
            lng: place.location.lng()
          }
          this.newBranch.geoFencingData.formatted_address = place.formatted_address
          this.newBranch.branchLocation.addressLine1 = place.formatted_address
          this.newBranch.branchLocation.city = place.city || ''
          this.newBranch.branchLocation.state = place.state || ''
          this.newBranch.branchLocation.pinCode = place.pincode || ''
          this.newBranch.branchLocation.country = place.country || ''
          this.newBranch.branchLocation.iso = place.countryCode || ''
          // Initialize map if geo-fence is enabled
          if (this.newBranch.geoFenceStatus) {
            this.$nextTick(() => {
              this.initBranchMap()
            })
          }
        }
        this.isBranchEdited = true
      } else {
        console.warn('Branch modal is not open or newBranch is not initialized yet')
      }
    },

    async getVoiceNumbers() {
      try {
        const response = await this.$axios.get('/tele-caller/credentials')
        if (response.data.success) {
          this.phoneNumbers = response?.data?.data
        }
      } catch (error) {
        console.log(error)
      }
    },

    async saveBranch() {
      if (!this.branchFormValid || this.isSaving) return

      try {
        this.isSaving = true
        //upload signature if available
        if (this.signatureFile) {
          await this.handleSignatureUpload(this.signatureFile)
        }

        const payload = {
          branchName: this.newBranch.name,
          branchDescription: this.newBranch.description,
          branchAddress: this.newBranch.address,
          geoFencingData: this.newBranch.geoFencingData || {},
          branchLocation: this.newBranch.branchLocation || {},
          geoFenceStatus: this.newBranch.geoFenceStatus || false,
          authorizedSignature: this.newBranch.authorizedSignature || '',
          branchHead: this.newBranch.branchHead || '' // Add branchHead to payload
        }

        let response
        let branchId
        let successMessage
        let isUpdate = !!this.newBranch.id

        if (isUpdate) {
          // Update existing branch
          response = await this.$axios.put(
            `${axiosUrl.branches.putBranches}/${this.newBranch.id}`,
            payload
          )
          branchId = this.newBranch.id
          successMessage = response?.data?.message || 'Branch updated successfully'

          // Update local state
          const branchIndex = this.branches.findIndex((b) => b._id === branchId)
          if (branchIndex !== -1) {
            this.branches[branchIndex] = {
              ...this.branches[branchIndex],
              branchName: this.newBranch.name,
              branchDescription: this.newBranch.description,
              branchAddress: this.newBranch.address,
              branchLocation: this.newBranch.branchLocation,
              geoFencingData: payload.geoFencingData,
              geoFenceStatus: payload.geoFenceStatus,
              authorizedSignature: this.newBranch.authorizedSignature || '',
              branchHead: this.newBranch.branchHead || '' // Add branchHead to local state
            }
          }
        } else {
          // Create new branch
          response = await this.$axios.post(axiosUrl.branches.postBranches, [payload])
          branchId = response.data.branch[0]._id

          const newBranch = {
            _id: branchId,
            branchName: this.newBranch.name,
            branchDescription: this.newBranch.description,
            branchAddress: this.newBranch.address,
            geoFencingData: payload.geoFencingData || {},
            branchLocation: this.newBranch.branchLocation || {},
            geoFenceStatus: payload.geoFenceStatus || false,
            teams: [],
            authorizedSignature: this.newBranch.authorizedSignature || '',
            branchHead: this.newBranch.branchHead || '' // Add branchHead to new branch
          }

          this.branches.push(newBranch)
          successMessage = response?.data?.message || 'Branch created successfully'
        }

        this.$toast.success(successMessage)
        this.closeBranchModal()
        const targetBranch = this.branches.find((branch) => branch._id === branchId)
        if (targetBranch) {
          this.selectBranch(targetBranch)
        }
      } catch (error) {
        console.error('Error saving branch:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to save branch')
      } finally {
        this.isSaving = false
      }
    },

    async saveTeam() {
      if (!this.teamFormValid || this.isSaving) return

      try {
        this.isSaving = true

        const payload = {
          teamName: this.newTeam.name,
          teamDescription: this.newTeam.description,
          branch: this.selectedBranch?._id,
          teamLead: this.newTeam.lead || null,
          members: this.members || [],
          connectVirtualNumber: this.newTeam.connectVirtualNumber,
          useCase: this.newTeam.useCase,
          subCase: this.newTeam.subCase
        }

        let response
        let isUpdate = !!this.newTeam.id

        if (isUpdate) {
          response = await this.$axios.put(`${axiosUrl.teams.putTeams}/${this.newTeam.id}`, payload)
        } else {
          response = await this.$axios.post(axiosUrl.teams.postTeams, [payload])
        }

        const successMessage =
          response.data.message || `Team ${isUpdate ? 'updated' : 'created'} successfully`
        this.$toast.success(successMessage)

        // Optimistically update local state
        if (isUpdate) {
          const teamIndex = this.teams.findIndex((t) => t._id === this.newTeam.id)
          if (teamIndex !== -1) {
            // Update the team in the teams array with all fields including useCase and subCase
            this.teams[teamIndex] = {
              ...this.teams[teamIndex],
              name: this.newTeam.name,
              teamName: this.newTeam.name,
              description: this.newTeam.description,
              lead: this.newTeam.lead || null,
              teamLead: this.newTeam.lead || null,
              members: this.members,
              connectVirtualNumber: this.newTeam.connectVirtualNumber,
              // dataCategories: this.newTeam.dataCategories,
              useCase: this.newTeam.useCase || '',
              subCase: this.newTeam.subCase || ''
            }
          }
        } else {
          const newTeamData = response.data.team || response.data.teams?.[0]
          const formattedTeam = {
            _id: newTeamData?._id || `temp_${Date.now()}`,
            id: newTeamData?._id || `temp_${Date.now()}`,
            name: this.newTeam.name,
            teamName: this.newTeam.name,
            description: this.newTeam.description || '',
            lead: this.newTeam.lead || null,
            teamLead: this.newTeam.lead || null,
            members: this.members || [],
            branch: this.selectedBranch?._id,
            connectVirtualNumber: this.newTeam.connectVirtualNumber,
            // dataCategories: this.newTeam.dataCategories || [],
            useCase: this.newTeam.useCase || '',
            subCase: this.newTeam.subCase || '',
            disabled: true
          }
          this.teams.push(formattedTeam)
        }

        // Update the teams in the selected branch
        if (this.selectedBranch) {
          const updatedTeams = this.getTeamsByBranch(this.selectedBranch._id)
          this.$set(this.selectedBranch, 'teams', updatedTeams)

          if (!isUpdate && updatedTeams.length > 0) {
            const newlyCreatedTeam = updatedTeams[updatedTeams.length - 1]
            this.selectTeam(newlyCreatedTeam)
          }
        }

        this.closeTeamModal()
      } catch (error) {
        console.error('Error saving team:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to save team')
      } finally {
        this.isSaving = false
      }
    },
    async fetchManager() {
      try {
        const response = await this.$axios.get('/users?roleAssigned=true')
        this.managers = response.data.users || []
        this.availableMembers = this.managers.map((manager) => manager.fullName)
      } catch (error) {
        console.log(error, 'Error fetching managers')
        this.availableMembers = []
      }
    },
    async fetchEmployees() {
      try {
        if (this.fetchOnSwitch) {
          this.fetchOnSwitch = false
          return
        }
        this.isLoading = true
        const response = await this.$axios.get(axiosUrl.employees.getEmployees)
        this.employees = response.data.users
        // console.log('Employees:', this.employees)
      } catch (error) {
        console.error('Error fetching employees:', error)
      } finally {
        this.isLoading = false
        this.loading = false
      }
    },
    editBranch(branch) {
      this.newBranch = {
        id: branch._id,
        name: branch.branchName,
        address: branch.branchLocation?.addressLine1 || '',
        description: branch.branchDescription,
        geoFencingData: branch.geoFencingData || {},
        branchLocation: branch.branchLocation || {},
        geoFenceStatus: branch.geoFenceStatus || false,
        authorizedSignature: branch.authorizedSignature || '',
        branchHead: branch.branchHead || '' // Add branchHead to edit form
      }
      this.isBranchEdited = false
      this.showBranchModal = true
    },
    editTeam(team) {
      this.newTeam = {
        id: team._id,
        name: team.name || team.teamName || '',
        description: team.description || '',
        lead: team.lead || team.teamLead || '',
        members: [...(team.members || [])],
        connectVirtualNumber: team.connectVirtualNumber || null,
        // dataCategories: [...(team.dataCategories || [])],

        useCase: team.useCase || '',
        subCase: team.subCase || ''
      }
      this.selectedUseCase = team.useCase || ''
      this.newMember = null
      this.isTeamEdited = false
      this.signatureFile = null
      this.showTeamModal = true
    },

    addBranch() {
      this.isBranchEdited = false
      this.showBranchModal = true
    },
    getMemberDetails(memberName) {
      if (!memberName) return {}

      const member = this.managers.find((manager) => manager.fullName === memberName)
      if (member) {
        return {
          name: member.fullName,
          profileImage: member.profileImage,
          gender: member.gender
        }
      }

      return {
        name: memberName
      }
    },
    async refreshBranchData() {
      // Refresh branches and teams data
      await this.fetchBranches()
      await this.fetchTeams()

      // Re-select the current branch to update its teams
      if (this.selectedBranch) {
        const updatedBranch = this.branches.find((branch) => branch._id === this.selectedBranch._id)
        if (updatedBranch) {
          this.selectBranch(updatedBranch)
        }
      }
    },

    // Add this new method to fetch all subcases for all use cases
    async fetchAllSubCasesForAllUseCases() {
      try {
        // Fetch subcases for all available use cases
        if (this.useCaseData && this.useCaseData.length > 0) {
          const allSubCasesPromises = this.useCaseData.map((useCase) =>
            this.$axios.get(`/orgSetup/extend-usecase?useCase=${useCase.value}`)
          )

          const responses = await Promise.all(allSubCasesPromises)

          // Combine all subcases into one array
          this.allSubCases = responses.flatMap((response, index) => {
            const subCases = response.data?.data || []
            const useCaseValue = this.useCaseData[index].value
            return subCases.map((subCase) => ({
              ...subCase,
              useCase: useCaseValue
            }))
          })

          // Set flag to indicate all subcases have been loaded
          this.hasAllSubCasesLoaded = true
        }
      } catch (error) {
        console.log(error)
        this.$toast.error('Failed to fetch all subcases')
      }
    },

    async loadGoogleMapsScript() {
      if (typeof google !== 'undefined' && google.maps) {
        this.googleMapsLoaded = true
        return Promise.resolve()
      }

      // Check if API key is available
      const apiKey = process.env.VUE_APP_MAP_KEY
      if (!apiKey) {
        console.error('Google Maps API key not found in environment variables')
        return Promise.reject(new Error('Google Maps API key not configured'))
      }

      return new Promise((resolve, reject) => {
        // Check if script is already being loaded
        if (window.branchTeamGoogleMapsLoading) {
          // Wait for existing load to complete
          window.branchTeamGoogleMapsCallbacks = window.branchTeamGoogleMapsCallbacks || []
          window.branchTeamGoogleMapsCallbacks.push(() => {
            this.googleMapsLoaded = true
            resolve()
          })
          return
        }

        // Mark as loading
        window.branchTeamGoogleMapsLoading = true
        window.branchTeamGoogleMapsCallbacks = []

        // Create script element
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=Function.prototype`
        script.async = true
        script.defer = true

        script.onload = () => {
          this.googleMapsLoaded = true

          // Execute callbacks
          window.branchTeamGoogleMapsCallbacks.forEach((callback) => callback())
          window.branchTeamGoogleMapsCallbacks = []
          window.branchTeamGoogleMapsLoading = false

          resolve()
        }

        script.onerror = () => {
          console.error('Failed to load Google Maps script for BranchTeam')
          window.branchTeamGoogleMapsLoading = false
          reject(new Error('Failed to load Google Maps script'))
        }

        document.head.appendChild(script)
      })
    },

    initBranchMap() {
      // Load Google Maps if not already loaded
      if (!this.googleMapsLoaded) {
        this.loadGoogleMapsScript()
          .then(() => {
            this.createMap()
          })
          .catch((error) => {
            console.error('Failed to load Google Maps:', error)
          })
        return
      }

      // Create map directly if Google Maps is already loaded
      this.createMap()
    },

    createMap() {
      // Check if we have a valid location
      if (!this.newBranch.geoFencingData || !this.newBranch.geoFencingData.location) {
        console.warn('No location data available')
        return
      }

      // Check if map container exists
      if (!this.$refs.branchMap) {
        console.warn('Map container not found')
        return
      }

      const location = {
        lat: this.newBranch.geoFencingData.location.lat,
        lng: this.newBranch.geoFencingData.location.lng
      }

      // Initialize map
      this.branchMap = new google.maps.Map(this.$refs.branchMap, {
        center: location,
        zoom: 15
      })

      // Add marker
      if (this.branchMapMarker) {
        this.branchMapMarker.setMap(null)
      }

      this.branchMapMarker = new google.maps.Marker({
        position: location,
        map: this.branchMap
      })

      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `<div><strong>${this.newBranch.address}</strong></div>`
      })

      infoWindow.open(this.branchMap, this.branchMapMarker)

      // Add circle for geofencing
      if (this.branchMapCircle) {
        this.branchMapCircle.setMap(null)
      }

      this.branchMapCircle = new google.maps.Circle({
        strokeColor: '#2A83FF',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#2A83FF',
        fillOpacity: 0.35,
        map: this.branchMap,
        center: location,
        radius: this.newBranch.geoFencingData.radius || 500
      })
    },

    updateMapRadius(value) {
      if (this.branchMapCircle) {
        this.branchMapCircle.setRadius(value)
      }

      if (this.newBranch.geoFencingData) {
        this.newBranch.geoFencingData.radius = value
      }

      this.isBranchEdited = true
    },

    // Watch for changes to geoFenceStatus
    watch: {
      'newBranch.geoFenceStatus': {
        handler(newVal) {
          if (newVal && this.newBranch.address) {
            this.$nextTick(() => {
              this.initBranchMap()
            })
          }
        }
      }
    },

    isGeoFeatureVisible(feature) {
      return isModuleFeatureAllowed('ATTENDANCE_MANAGEMENT', feature)
    },

    // Handle use case selection
    handleUseCaseChange(useCase) {
      this.selectedUseCase = useCase
      this.newTeam.useCase = useCase
      // Reset subCase when useCase changes
      this.newTeam.subCase = ''
    },

    // Helper method to get subcase name by ID
    getSubCaseNameById(subCaseId) {
      if (!subCaseId || !this.allSubCases || this.allSubCases.length === 0) return ''
      const subCase = this.allSubCases.find((sc) => sc._id === subCaseId)
      return subCase ? subCase.name : ''
    },

    // Navigate to telecaller integration settings
    navigateToTelecallerIntegration() {
      this.showTeamModal = false
      this.$router
        .replace({
          name: 'Settings',
          query: {
            activeTab: 'OrgConfig',
            subActiveTab: 'platformIntegrations'
          }
        })
        .catch((err) => {
          console.error('Navigation error:', err)
          // Even if there's an error, we've closed the modal
        })
    },

    // Add signature upload handler

    async handleSignatureUpload(file) {
      if (!file) {
        this.newBranch.authorizedSignature = null
        this.isTeamEdited = true
        return
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
      if (!validTypes.includes(file.type)) {
        this.$toast.error('Please upload a valid image file (JPEG, PNG)')
        this.signatureFile = null
        return
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error('File size should be less than 5MB')
        this.signatureFile = null
        return
      }

      try {
        const formData = new FormData()
        formData.append('attachments', file)

        const response = await this.$axios.post('/authorized/signature', formData)

        this.newBranch.authorizedSignature = response.data.url?.doc_physical_path
        this.isTeamEdited = true
      } catch (error) {
        console.error('Error uploading signature:', error)
        this.$toast.error('Failed to upload signature')
        this.signatureFile = null
      }
    },

    // Add method to remove signature
    removeSignature() {
      this.newBranch.authorizedSignature = null
      this.signatureFile = null
      this.isTeamEdited = true
    },

    // Add method to format signature URL with S3 base URL
    getFormattedSignatureUrl(signatureUrl) {
      if (!signatureUrl) return ''

      // If the signature already contains a full URL, return as is
      if (signatureUrl.startsWith('http')) {
        return signatureUrl
      }

      // Otherwise, prepend the S3 base URL
      return `${this.baseUrl || ''}${signatureUrl}`
    }
  }
}
</script>

<style scoped>
/* Simple overflow management like Roles.vue */
.overflow-hidden {
  overflow: hidden !important;
}

.overflow-y-auto {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.overflow-x-hidden {
  overflow-x: hidden !important;
}

/* Team item hover effect */
.team-item-hover:hover {
  background-color: #f7fafc;
  border-color: #e2e8f0;
}

/* Member divider styling */
.member-divider {
  border-bottom: 1px solid #e2e8f0;
}

.member-divider:last-child {
  border-bottom: none;
}

/* Ensure all Vuetify components have proper spacing for labels */
.v-text-field,
.v-textarea,
.v-select,
.v-autocomplete {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
}

/* Prevent label cutoff by ensuring adequate space */
.v-text-field .v-label,
.v-select .v-label,
.v-autocomplete .v-label {
  transform-origin: top left !important;
}

/* Ensure form fields have proper height and spacing */
.v-input--is-focused .v-label,
.v-input--has-state .v-label {
  transform: translateY(-18px) scale(0.75) !important;
}

/* Dialog form content with proper spacing */
.form-content .v-input {
  margin-top: 4px !important;
  margin-bottom: 4px !important;
}
.form-card {
  border: 1px solid #e0e0e0;
  border-radius: 0px !important;
}

/* Remove excessive overflow hidden that causes label cutoff */
.v-text-field,
.v-textarea,
.v-select,
.v-autocomplete,
.v-input {
  overflow: visible !important;
}

.v-text-field >>> .v-input__control,
.v-textarea >>> .v-input__control,
.v-select >>> .v-input__control,
.v-autocomplete >>> .v-input__control {
  overflow: visible !important;
}

/* Dialog content overflow management with proper spacing */
.v-dialog .v-card {
  overflow: visible !important;
}
.v-dialog .v-card .v-card_title {
  overflow: hidden !important;
  padding: 0 !important;
}

.v-dialog .v-card-text {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* Ensure form input fields have proper spacing for labels */
.v-input__slot {
  overflow: visible !important;
  padding-top: 8px !important;
}

.v-select__selections {
  overflow: hidden !important;
}

.v-chip {
  overflow: hidden !important;
}

/* Card content overflow */
.v-card-text {
  overflow: hidden !important;
}

/* List items overflow */
.space-y-2 > *,
.space-y-3 > * {
  overflow: hidden !important;
}
.form-title {
  font-size: 1.25rem;
  padding: 0px !important;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}

.inline-flex.items-center.bg-blue-100.rounded-full {
  display: inline-flex !important;
  align-items: center !important;
  background-color: #dbeafe !important;
  border-radius: 9999px !important;
  /* padding: 0.25rem 0.75rem !important; */
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: #1e40af !important;
}
.v-application p {
  margin-bottom: 0px !important;
}
</style>
